import { useSettings } from '../../stores/settings-store';

export type SfxName =
  | 'ui_click'
  | 'ui_confirm'
  | 'ui_error'
  | 'sword_swing'
  | 'sword_swing_heavy'
  | 'hit'
  | 'crit'
  | 'skill_thrust'
  | 'skill_wave'
  | 'skill_formation'
  | 'wolf_bite'
  | 'heavy_impact'
  | 'player_hurt'
  | 'dodge'
  | 'pickup'
  | 'coins'
  | 'level_up'
  | 'quest_accept'
  | 'quest_complete'
  | 'equip'
  | 'heal'
  | 'death'
  | 'footstep'
  | 'teleport'
  | 'monster_die';

/**
 * Web Audio manager. Every sound is synthesised at runtime (no audio files to license):
 * filtered-noise swooshes, membrane thumps, metallic rings and a generative pentatonic score.
 * The context is created on the first user gesture to satisfy browser autoplay policies.
 */
class AudioManager {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private musicBus!: GainNode;
  private sfxBus!: GainNode;
  private noise!: AudioBuffer;
  private musicTimer: number | null = null;
  private nextNoteAt = 0;
  private ambience: { stop(): void } | null = null;
  private lastPlayed = new Map<string, number>();

  constructor() {
    useSettings.subscribe(() => this.applyVolumes());
  }

  get ready(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  unlock(): void {
    if (!this.ctx) {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.musicBus = this.ctx.createGain();
      this.sfxBus = this.ctx.createGain();
      const comp = this.ctx.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.ratio.value = 4;
      this.musicBus.connect(this.master);
      this.sfxBus.connect(this.master);
      this.master.connect(comp).connect(this.ctx.destination);
      const len = this.ctx.sampleRate;
      this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const d = this.noise.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      this.applyVolumes();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  private applyVolumes(): void {
    if (!this.ctx) return;
    const s = useSettings.getState();
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(s.muted ? 0 : s.masterVolume, t, 0.05);
    this.musicBus.gain.setTargetAtTime(s.musicVolume * 0.5, t, 0.05);
    this.sfxBus.gain.setTargetAtTime(s.sfxVolume, t, 0.05);
  }

  // ---------------------------------------------------------------- primitives

  private env(g: GainNode, t: number, attack: number, peak: number, decay: number): void {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  }

  private noiseBurst(
    t: number,
    dur: number,
    f0: number,
    f1: number,
    q: number,
    peak: number,
    type: BiquadFilterType = 'bandpass',
    dest: AudioNode = this.sfxBus,
  ): void {
    const ctx = this.ctx as AudioContext;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.Q.value = q;
    f.frequency.setValueAtTime(f0, t);
    f.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    const g = ctx.createGain();
    this.env(g, t, Math.min(0.02, dur * 0.2), peak, dur);
    src.connect(f).connect(g).connect(dest);
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.05);
  }

  private tone(
    t: number,
    type: OscillatorType,
    f0: number,
    f1: number,
    dur: number,
    peak: number,
    dest: AudioNode = this.sfxBus,
    attack = 0.005,
  ): void {
    const ctx = this.ctx as AudioContext;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    const g = ctx.createGain();
    this.env(g, t, attack, peak, dur);
    o.connect(g).connect(dest);
    o.start(t);
    o.stop(t + attack + dur + 0.05);
  }

  /** Plucked string (guzheng-like) for music and chimes. */
  private pluck(t: number, freq: number, peak: number, decay: number, dest: AudioNode): void {
    const ctx = this.ctx as AudioContext;
    const g = ctx.createGain();
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(freq * 6, t);
    lp.frequency.exponentialRampToValueAtTime(freq * 1.5, t + decay);
    this.env(g, t, 0.004, peak, decay);
    for (const [mult, type, amp] of [
      [1, 'triangle', 1],
      [2.003, 'sine', 0.35],
      [3.01, 'sine', 0.12],
    ] as [number, OscillatorType, number][]) {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = freq * mult;
      const og = ctx.createGain();
      og.gain.value = amp;
      o.connect(og).connect(lp);
      o.start(t);
      o.stop(t + decay + 0.1);
    }
    lp.connect(g).connect(dest);
  }

  play(name: SfxName, opts: { volume?: number; pitch?: number } = {}): void {
    if (!this.ctx || this.ctx.state !== 'running') return;
    const now = this.ctx.currentTime;
    // Rate-limit identical sounds (e.g. many hits in one frame).
    const last = this.lastPlayed.get(name) ?? 0;
    if (now - last < 0.035) return;
    this.lastPlayed.set(name, now);
    const v = opts.volume ?? 1;
    const p = opts.pitch ?? 1;
    const t = now + 0.005;
    switch (name) {
      case 'ui_click':
        this.tone(t, 'sine', 1400 * p, 900 * p, 0.05, 0.08 * v);
        break;
      case 'ui_confirm':
        this.pluck(t, 587 * p, 0.18 * v, 0.6, this.sfxBus);
        this.pluck(t + 0.08, 880 * p, 0.14 * v, 0.8, this.sfxBus);
        break;
      case 'ui_error':
        this.tone(t, 'square', 220, 180, 0.12, 0.05 * v);
        break;
      case 'sword_swing':
        this.noiseBurst(t, 0.16, 1800 * p, 4200 * p, 1.2, 0.35 * v);
        break;
      case 'sword_swing_heavy':
        this.noiseBurst(t, 0.26, 900 * p, 3400 * p, 0.9, 0.45 * v);
        break;
      case 'hit':
        this.tone(t, 'sine', 150 * p, 55, 0.14, 0.45 * v);
        this.noiseBurst(t, 0.06, 2500, 900, 0.8, 0.25 * v);
        break;
      case 'crit':
        this.tone(t, 'sine', 170 * p, 50, 0.2, 0.55 * v);
        this.noiseBurst(t, 0.08, 3000, 800, 0.8, 0.3 * v);
        this.tone(t + 0.01, 'triangle', 1760, 1700, 0.5, 0.06 * v);
        break;
      case 'skill_thrust':
        this.noiseBurst(t, 0.22, 600, 5200, 2.5, 0.4 * v);
        this.tone(t + 0.02, 'sawtooth', 220, 880, 0.18, 0.05 * v);
        break;
      case 'skill_wave':
        this.noiseBurst(t, 0.5, 400, 2600, 1.5, 0.35 * v, 'lowpass');
        this.tone(t, 'sine', 523, 1046, 0.45, 0.08 * v);
        this.tone(t + 0.05, 'sine', 784, 1568, 0.4, 0.05 * v);
        break;
      case 'skill_formation':
        this.tone(t, 'sine', 90, 40, 0.9, 0.6 * v);
        this.noiseBurst(t, 0.6, 300, 80, 0.7, 0.35 * v, 'lowpass');
        [0, 0.07, 0.14, 0.21].forEach((d, i) =>
          this.pluck(t + d, [587, 659, 880, 988][i] as number, 0.12 * v, 1.2, this.sfxBus),
        );
        break;
      case 'wolf_bite':
        this.tone(t, 'sawtooth', 160 * p, 90, 0.18, 0.12 * v);
        this.noiseBurst(t, 0.07, 1200, 500, 1, 0.25 * v);
        break;
      case 'heavy_impact':
        this.tone(t, 'sine', 80, 30, 0.6, 0.7 * v);
        this.noiseBurst(t, 0.4, 600, 60, 0.6, 0.45 * v, 'lowpass');
        break;
      case 'player_hurt':
        this.tone(t, 'triangle', 200, 120, 0.15, 0.12 * v);
        break;
      case 'dodge':
        this.noiseBurst(t, 0.2, 500, 2500, 0.7, 0.22 * v);
        break;
      case 'pickup':
        this.pluck(t, 1046, 0.12 * v, 0.4, this.sfxBus);
        this.pluck(t + 0.06, 1318, 0.1 * v, 0.5, this.sfxBus);
        break;
      case 'coins':
        [0, 0.05, 0.1].forEach((d) =>
          this.tone(t + d, 'triangle', 2600 + Math.random() * 600, 2400, 0.12, 0.05 * v),
        );
        break;
      case 'level_up':
        [0, 0.1, 0.2, 0.3, 0.45].forEach((d, i) =>
          this.pluck(t + d, [587, 659, 740, 880, 1175][i] as number, 0.16 * v, 1.5, this.sfxBus),
        );
        this.tone(t, 'sine', 110, 108, 2.2, 0.25 * v, this.sfxBus, 0.02);
        break;
      case 'quest_accept':
        this.pluck(t, 740, 0.14 * v, 0.9, this.sfxBus);
        this.pluck(t + 0.12, 988, 0.12 * v, 1.0, this.sfxBus);
        break;
      case 'quest_complete':
        [0, 0.12, 0.24, 0.42].forEach((d, i) =>
          this.pluck(t + d, [880, 740, 988, 1175][i] as number, 0.16 * v, 1.3, this.sfxBus),
        );
        break;
      case 'equip':
        this.tone(t, 'triangle', 900, 1200, 0.08, 0.08 * v);
        this.noiseBurst(t, 0.08, 4000, 2000, 2, 0.1 * v);
        break;
      case 'heal':
        this.tone(t, 'sine', 660, 990, 0.5, 0.08 * v);
        this.tone(t + 0.1, 'sine', 880, 1320, 0.5, 0.06 * v);
        break;
      case 'death':
        this.tone(t, 'sine', 220, 70, 1.2, 0.2 * v);
        break;
      case 'monster_die':
        this.tone(t, 'sawtooth', 180 * p, 60, 0.4, 0.06 * v);
        break;
      case 'footstep':
        this.noiseBurst(t, 0.05, 900 * p, 300, 0.8, 0.05 * v, 'lowpass');
        break;
      case 'teleport':
        this.noiseBurst(t, 0.8, 200, 6000, 3, 0.2 * v);
        [0, 0.1, 0.2].forEach((d, i) =>
          this.pluck(t + d, [1175, 1480, 1760][i] as number, 0.1 * v, 1, this.sfxBus),
        );
        break;
    }
  }

  // ---------------------------------------------------------------- music & ambience

  /** Generative score: plucked pentatonic phrases over a soft drone (D gong mode). */
  startMusic(): void {
    if (!this.ctx || this.musicTimer !== null) return;
    const ctx = this.ctx;
    const scale = [293.66, 329.63, 369.99, 440, 493.88, 587.33, 659.25, 739.99, 880];
    const droneGain = ctx.createGain();
    droneGain.gain.value = 0;
    droneGain.gain.setTargetAtTime(0.06, ctx.currentTime, 2);
    droneGain.connect(this.musicBus);
    const drones = [73.42, 110, 146.83].map((f) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f;
      o.connect(droneGain);
      o.start();
      return o;
    });
    let idx = 4;
    this.nextNoteAt = ctx.currentTime + 0.5;
    const schedule = (): void => {
      while (this.nextNoteAt < ctx.currentTime + 0.6) {
        const restChance = Math.random();
        if (restChance > 0.18) {
          idx = Math.max(0, Math.min(scale.length - 1, idx + Math.round((Math.random() - 0.5) * 4)));
          this.pluck(
            this.nextNoteAt,
            scale[idx] as number,
            0.09 + Math.random() * 0.05,
            1.6 + Math.random(),
            this.musicBus,
          );
          if (Math.random() < 0.15)
            this.pluck(this.nextNoteAt + 0.09, (scale[idx] as number) * 2, 0.04, 1.2, this.musicBus);
        }
        this.nextNoteAt += [0.42, 0.42, 0.84, 0.63, 1.26][Math.floor(Math.random() * 5)] as number;
      }
    };
    schedule();
    this.musicTimer = window.setInterval(schedule, 200);
    this.stopMusicFn = () => {
      droneGain.gain.setTargetAtTime(0, ctx.currentTime, 0.5);
      drones.forEach((o) => o.stop(ctx.currentTime + 2));
    };
  }

  private stopMusicFn: (() => void) | null = null;

  stopMusic(): void {
    if (this.musicTimer !== null) window.clearInterval(this.musicTimer);
    this.musicTimer = null;
    this.stopMusicFn?.();
    this.stopMusicFn = null;
  }

  /** Wind + occasional bird calls. */
  startAmbience(): void {
    if (!this.ctx || this.ambience) return;
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 420;
    const g = ctx.createGain();
    g.gain.value = 0.035;
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.08;
    lfoGain.gain.value = 0.02;
    lfo.connect(lfoGain).connect(g.gain);
    src.connect(lp).connect(g).connect(this.sfxBus);
    src.start();
    lfo.start();
    const birds = window.setInterval(() => {
      if (Math.random() < 0.35) {
        const t = ctx.currentTime;
        const base = 2200 + Math.random() * 1600;
        for (let i = 0; i < 2 + Math.floor(Math.random() * 3); i++)
          this.tone(t + i * 0.11, 'sine', base, base * 1.3, 0.07, 0.015);
      }
    }, 2500);
    this.ambience = {
      stop: () => {
        src.stop();
        lfo.stop();
        window.clearInterval(birds);
      },
    };
  }

  stopAmbience(): void {
    this.ambience?.stop();
    this.ambience = null;
  }
}

export const audio = new AudioManager();
